import React from "react";
import {
    Box,
    Skeleton,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ResponsiveDetailLayout from "../../components/Layout/ResponsiveDetailLayout";

const ArtworkHeaderSkeleton: React.FC = () => (
    <Box>
        <Skeleton variant="rounded" height={56} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="text" height={40} width="60%" />
        <Skeleton variant="text" height={28} width="85%" />
        <Skeleton variant="text" height={24} width="70%" />
    </Box>
);

const ArtworkPrimaryMediaSkeleton: React.FC = () => (
    <Box
        sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            bgcolor: "background.paper",
        }}
    >
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
                gap: 1,
            }}
        >
            <Skeleton variant="text" width={120} height={32} />
            <Skeleton variant="rounded" width={120} height={32} />
        </Box>

        <Skeleton
            variant="rounded"
            height={320}
            sx={{ borderRadius: 2 }}
        />

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Skeleton variant="rounded" width={72} height={72} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={72} height={72} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={72} height={72} sx={{ borderRadius: 2 }} />
        </Stack>
    </Box>
);

const ArtworkDetailsSkeleton: React.FC = () => (
    <Box
        sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            bgcolor: "background.paper",
        }}
    >
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 1,
                flexWrap: "wrap",
            }}
        >
            <Skeleton variant="text" width={160} height={32} />
            <Skeleton variant="rounded" width={110} height={32} />
        </Box>

        <Stack spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
                <Box
                    key={i}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "90px 1fr", sm: "120px 1fr" },
                        gap: 1,
                        alignItems: "center",
                    }}
                >
                    <Skeleton variant="text" width="70%" height={22} />
                    <Skeleton variant="text" width="90%" height={22} />
                </Box>
            ))}
        </Stack>
    </Box>
);

const ArtworkSupportingSkeleton: React.FC = () => (
    <Box
        sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            bgcolor: "background.paper",
        }}
    >
        <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Skeleton variant="rounded" width={110} height={40} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={90} height={40} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
        </Stack>

        <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
    </Box>
);

const ArtworkPageSkeleton: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const header = <ArtworkHeaderSkeleton />;
    const primaryMedia = <ArtworkPrimaryMediaSkeleton />;
    const primaryDetails = <ArtworkDetailsSkeleton />;
    const secondaryContent = <ArtworkSupportingSkeleton />;

    if (isMobile) {
        return (
            <ResponsiveDetailLayout
                header={header}
                primaryMedia={primaryMedia}
                primaryDetails={primaryDetails}
                secondaryContent={secondaryContent}
                spacing={2}
            />
        );
    }

    return (
        <>
            {header}

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2.5 }}>
                <Box sx={{ width: "100%" }}>
                    <Box className="artwork-page">
                        <Box className="artwork-layout">
                            {primaryMedia}
                            {primaryDetails}
                            {secondaryContent}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default ArtworkPageSkeleton;